import 'process';

// CER-25361: set the MFE override global BEFORE certifyui's SPA loads. The
// JSON literal is injected by `rspack.DefinePlugin` (see
// `webpack/common.rspack.js`); in prod the object is `{}` because no
// `MFE_*` env vars are set. certifyui's `getRemoteEntryUrl` reads this and
// uses any matching entry instead of the CDN URL — preserving the local-dev
// workflow where you point a remote at e.g. `MFE_STATIC_PAGES=https://localhost:3002`.
(window as unknown as { __MFE_OVERRIDES__: Record<string, string> }).__MFE_OVERRIDES__ =
    JSON.parse(process.env.MFE_OVERRIDES_JSON || '{}');

import '@/tailwind.css';
import '@/scss/bundle.scss';

// Import jQuery and jQuery UI. Make jQuery and $ available as globals
import '@/util/jquery-global';
import isNull from 'lodash/isNull';
import { logger } from '@/util/logger';

import '@/modules/globals.js';
import '@/modules/utilities.js';
// --------------------------------------
// Legacy scripts that should go away eventually
import '@/modules/changeUserControl.js';
import '@/modules/columnSizer.js'; // set responsive column sizes for old style tables
import '@/modules/scrollFix.js'; // update panel hack to preserve scroll position
import '@/modules/popupFunc.js'; // flyout logic

// import and expose all modules
import Alert from '@/modules/alert.js';
import DateRangeSelector from '@/modules/dateRangeSelector.js';
import Dialog from '@/modules/dialog.js';
import ExpEdit from '@/modules/expEdit';
import Feed from '@/modules/feed.js';
import FileUpload from '@/modules/fileUpload.js';
import FocusGuard from '@/modules/focusGuard.js';
import loader from '@/modules/loader.js';
import * as microNav from '@/modules/micronav.js';
import MultiSelect from '@/modules/multiSelect.js';
import {
    asyncAppendPreviewImage,
    asyncGetPDFPreviewImageURL,
    getViewerSrc,
} from '~/Scripts/pdfjs/build/pdf.js';
import ProgBar from '@/modules/progBar.js';
import ProgressBar from '@/modules/progressBar.js';
import * as prm from '@/modules/prm.js';
import Queue from '@/modules/queue.js';
import '@/modules/receiptViewer';
import ResponsiveTable from '@/modules/responsiveTable';
import ResponsiveTable2 from '@/modules/responsiveTable2.js';
import * as scrollJump from '@/modules/scrollJump.js';
import Spinner from '@/modules/spinner.js';
import Validation from '@/modules/validation.js';
import SuggestSelectionAsync from '@/modules/suggestSelectionAsync.js';
import '@/modules/spendTrackerInit.js'; // exposes global methods
import { characterCounter } from '@/modules/characterCounter.js'; //textbox character counter
import '@/modules/componentLoader'; // exposed as jQuery plugin
import {
    alertToast,
    confirmToast,
    neutralToast,
} from '@/modules/toast';
import { DynamicFilterController } from '@/modules/DynamicFilterController';
import {
    mountAmazonConnector,
    mountLoggedOutEmbarkHeader,
 } from '@certifyinc/certifyui/dist/shared/components';
// #endregion

import vueInit, { VueSettings } from '@/vue/shared/vueInit';
/*
    expose embark render methods to window so that they may be
    called on webforms pages.
*/
import { renderReactView, renderEmbark } from '@certifyinc/certifyui/dist/shared/utils/renderReactView';
import dayjs from 'dayjs';
import { AccountConfig } from './interfaces/AccountConfig';
import { isUserLoggedIn } from './util/helpers';

import { saveUserPreference } from '@/api/api';
import { getWebUIEnvironment, WebUIEnvironments} from './util/environment';

import { SentryConfig } from '@/interfaces/SentryConfig';
import { sentryInit } from '@/util/sentryInit';

import {mountSiteFooter} from '@/react/mountSiteFooter';

import { registerAssuranceMoreDetailsHandler } from '@/modules/assuranceMoreDetails';

// Load wc-* tag definitions from the components MFE at runtime. Replaces the
// previous static `registerAll()` from `@/react/registerWebComponents` (deleted).
// Source for the wc-* widgets now lives in `emburse-pro-mfe-components` so
// changes ship via that MFE's CDN deploy without a CertifyApp redeploy.
// @ts-expect-error — federated module at runtime, no ambient types in CertifyApp
import('components/register')
    .then(({ register }) => register())
    .catch((err) => logger.error('Failed to load components MFE for wc-* tags', err));

// Click delegate for "More Details" links emitted by ExpRptAppr's CreateAuditInfoText.
// Dispatches assurance:open-approver-modal CustomEvent on document; the wc-assurance-audit-modal
// in approver variant listens for it and opens with the matching expense.
registerAssuranceMoreDetailsHandler();

// import css
require('jquery-ui-bundle/jquery-ui.css');

require('velocity-animate'); // spendsmart logic

// -------------------------------------

declare let window: any;

declare global {
    interface Window {
        AccountConfig: AccountConfig,
        SentryConfig: SentryConfig,
        applyLoadingState: Function,
        removeLoadingState: Function,
    }
}
window.Alert = Alert;
window.DateRangeSelector = DateRangeSelector;
window.Dialog = Dialog;
window.ExpEdit = ExpEdit;
window.Feed = Feed;
window.FileUpload = FileUpload;
window.FocusGuard = FocusGuard;
window.loader = loader;
window.microNav = microNav;
window.MultiSelect = MultiSelect;
window.pdfService = {
    asyncAppendPreviewImage,
    asyncGetPDFPreviewImageURL,
    getViewerSrc,
};
window.ProgBar = ProgBar;
window.ProgressBar = ProgressBar;
window.prm = prm;
window.Queue = Queue;
window.ResponsiveTable = ResponsiveTable;
window.ResponsiveTable2 = ResponsiveTable2;
window.scrollJump = scrollJump;
window.Spinner = Spinner;
window.Validation = Validation;
window.SuggestSelectionAsync = SuggestSelectionAsync;
window.characterCounter = characterCounter;
window.alertToast = alertToast;
window.confirmToast = confirmToast;
window.neutralToast = neutralToast;
window.DynamicFilterController = DynamicFilterController;

window.SaveUserPreference = saveUserPreference;

// --------------------------------------------------------------------------------------------------------
// --------------------------------------------------------------------------------------------------------
// STARTUP SCRIPTS
// window.breakpoints = {
//	xsmall: 481,
//	small: 601,
//	medium: 769,
//	large: 900,
//	xlarge: 1281
// };

if (window.isFF) {
    // Fix for this issue https://stackoverflow.com/questions/14533585/jqueryui-dialog-firefox-asp-net-access-to-strict-mode-caller-function-is-c
    // Firefox ESR does not like the way we are calling __doPostBack from customConfirms
    window.event = {};
}

// Create layout queue
window.layoutQueue = new Queue('layoutQueue');
window.applyUIDebounced = window.debounce(() => {
    window.layoutQueue.run.call(window.layoutQueue);
}, 250);

interface EJQuery extends JQuery<HTMLElement> {
    selectToMultiSelect: () => void;
}

window.__doPostBackAsync = function(eventTarget) {
  const prm = window.Sys.WebForms.PageRequestManager.getInstance();

  return new Promise((resolve, reject) => {
    let done = false;

    function cleanup() {
      prm.remove_endRequest(onEndRequest);
    }

    function finish(success, value) {
      if (done) return;
      done = true;
      cleanup();
      success ? resolve(value) : reject(value);
    }

    function onEndRequest(sender, args) {
      finish(true, { sender, args });
    }

    prm.add_endRequest(onEndRequest);
    window.__doPostBack(eventTarget, '');
  });
}

// #region onready
$(() => {
    // do layout scripts
    window.layoutQueue.add('doColumnSizingKicker');

    window.loadBrowserIsHighDensity(); // util

    // Attach PRM begin request events

    // attach apply loading state unless implicit loading state has been disabled
    if (!window.disableImplicitLoadingState) {
        prm.brQueue.add('applyLoadingState');
    }

    // Attach PRM end request events
    prm.erQueue.add('applyTextLinkBorderBottom');
    prm.erQueue.add('run', window.layoutQueue);

    // track positions for maintain scroll position panels
    prm.erQueue.add('trackScrollPositions');

    // insert this here, in case we need to modify the tracked position of a maintain scroll position panel
    prm.erQueue.add('init', scrollJump);

    // restore positions for maintain scroll position panels
    prm.erQueue.add('restoreScrollPositions');
    prm.erQueue.add('animateAllLoadHighlights');

    window.animateAllLoadHighlights(); // utilities

    microNav.init();

    // Convert multiselect dropdowns
    $('.js-selectToMultiSelect').each(function () {
        ($(this) as EJQuery).selectToMultiSelect();
    });

    // Prevent autocomplete on infragisticsDateChooser input fields
    Array.from(document.querySelectorAll('.infragisticsDateChooser')).forEach((node) => {
        const input = node.querySelector('input');
        if (input) {
            input.setAttribute('autocomplete', 'off');
        }
    });

    // Hide info popups in reports when parent panel is scrolled
    if ($('.js-expenses-scroll').length) {
        $('.js-expenses-scroll').on('scroll', () => {
            if (!window.lastPopupId) {
                return;
            }
            window.hideLastPopup();
        });
    } else if ($('#MainContent_ExpensesTextPanelContent').length) {
        $('#MainContent_ExpensesTextPanelContent').on('scroll', () => {
            if (!window.lastPopupId) {
                return;
            }
            window.hideLastPopup();
        });
    }

    // Toggle Input. NOTE: This is currently only used to merge/unmerge on feeds. Should be modularized
    $(document).on('click change', '.js-toggle-input', function () {
        // Do not allow toggle to be switched again before page reloads
        if (
            $(this)
                .parents()
                .hasClass('is-in-progress')
        ) {
            return;
        }

        if ($(this).data().hiddenFieldId) {
            const hf = <HTMLInputElement>document.getElementById($(this).data().hiddenFieldId);
            if (hf) {
                hf.value = this.checked ? '1' : '0';
            }
        }

        $(this)
            .parents('.toggle')
            .addClass('is-in-progress');
    });

    $(document).on('click', '.is-in-progress .js-toggle-activator', (e) => {
        e.preventDefault();
    });

    // Animate scroll to top when clicking on links with anchor set to #top
    $('a[href="#top"]').click(() => {
        $('html, body').animate({
            scrollTop: 0,
        }, 'fast');
        return false;
    });

    // Prevent disabled links from executing
    $(document).on('click', 'a.is-disabled', (e) => {
        e.preventDefault();
    });

    // prevent disabled buttons from executing
    $(document).on('click', '.btn.is-disabled', (e) => {
        e.preventDefault();
    });

    // make buttons disable after one click
    $(document).on('click', '.js-click-and-disable', function () {
        // clear "are you sure you want to navigate away" message
        window.onbeforeunload = null;

        // set server-side values for this action
        const objDBA = <HTMLInputElement>document.getElementById('DoButtonAction');
        if (objDBA) {
            objDBA.value = '1';
        }

        // trigger loading animation because right now, this is ONLY being used with postbacks.
        // If that ever changes, we will have to add a conditional check here
        window.applyLoadingState($(this).attr('timeout'));

        // The logic to set the button to disabled is wrapped in a set timeout to
        // give the browser time to finish processing the click event. If the button is
        // disabled immediately, the click event will not be processed and the postback
        // will not occur.
        window.setTimeout(() => {
            $(this)
                .addClass('btn--disabled is-disabled')
                .attr('disabled', 'disabled')
                .trigger('blur')
                .removeClass('js-click-and-disable');
        }, 20);

        // if the current element has data-client-submit, then find and submit the form.
        // this allows backend code to force a client submit to trigger a full postback
        const shouldSubmit = this.dataset.clientSubmit;
        if (shouldSubmit) {
            const form = document.forms && document.forms[0]
            if (form) {
                form.submit();
            } else {
                logger.warn('No form found to submit on click-and-disable');
            }
        }
    });

    // Toggle a server-side setting
    // TODO: This is bad and should be re-architected.
    $(document).on('click', '.js-set', function (e) {
        e.preventDefault();

        const $this = $(this);

        if ($this.hasClass('is-pending')) {
            // a request is already in progress, Don't hit the server again until the first one completes.
            return;
        }
        const d = $this.data();
        const { settingName } = d;
        const settingValue = d.settingValue || 'toggle';
        const serviceUrl = d.serviceUrl || window.serviceUrl || null;
        const data = {};

        if (!settingName) {
            logger.warn(`no setting name defined for ${this.id}`);
            return;
        }
        if (!serviceUrl) {
            logger.warn(
                'could not update setting for',
                this.id,
                'because service URL is undefined',
            );
            return;
        }

        data[settingName] = settingValue;

        $this.addClass('is-pending');

        // set preference
        $.ajax({
            type: 'POST',
            url: serviceUrl,
            data,
        })
            .done(() => {
                // raise success
                if (typeof window[`${settingName}_onSuccess`] === 'function') {
                    window[`${settingName}_onSuccess`]($this);
                }
            })
            .fail(() => {
                // raise fail
                logger.log('fail');
                if (typeof window[`${settingName}_onFail`] === 'function') {
                    window[`${settingName}_onFail`]($this);
                }
            })
            .always(() => {
                $this.removeClass('is-pending');
                // raise always
                if (typeof window[`${settingName}_onAlways`] === 'function') {
                    window[`${settingName}_onAlways`]($this);
                }
            });
    });

    // hack to set event target for ImageButton postbacks
    $('input[type=image]').on('click', function () {
        const hfTarget = <HTMLInputElement>$('#__EVENTTARGET')[0];
        if (hfTarget && !hfTarget.value) {
            hfTarget.value = this.id;
        }
    });

    // daterange selector auto-init
    $('.js-daterange-select').each(function () {
        const d = new DateRangeSelector(this.id);
        d.init();
    });

    // Very simple tabbed navigation
    // TODO: should be abscrated into a module
    $(document).on('click', '.js-tab', function () {
        const $tab = $(this);

        if ($tab.hasClass('is-active')) {
            return;
        }

        const $target = $($tab.data().target);
        const $prevTab = $tab.siblings('.is-active');
        const $prevTarget = $($prevTab.data().target);

        // hide previous tab window
        $prevTab.removeClass('is-active');
        $prevTarget.addClass('is-hidden');

        // show new tab window
        $tab.addClass('is-active');
        $target.removeClass('is-hidden');
    });

    window.applyTextLinkBorderBottom();

    window.layoutQueue.run();

    window.applyUIDebounced();

    scrollJump.init();
});
// #endregion
// #region onload
$(window).on('load', () => {
    window.cancelScrollPositionPostbackReset = false;
    let sp = null;
    let wh = 0;

    prm.init();

    const hasCustomScroll = function () {
        return scrollJump.positions && scrollJump.positions.hasOwnProperty('document');
    };

    window.doSetClientInfoPost(window.getWindowWidth());

    const instance = prm.getInstance();

    // apply scroll position from pageloading state of partial postback lifecycle.
    // this makes it so that if you scroll the page while a postback is processing
    // it will not jump you back to the scroll position of where the postback began.
    if (instance) {
        instance.add_pageLoading(() => {
            // if we have set a scroll jump position for document, do not revert scroll position
            if (hasCustomScroll()) {
                return;
            }

            sp = $(window).scrollTop();
            wh = document.documentElement.scrollHeight;
        });

        instance.add_pageLoaded(() => {
            // When The draft page columns are sized, the page height can change between pageLoaded and endRequest on
            // slower browsers/machines when in detailed view. So if we are at or near the bottom of the page, wait until
            // endRequest to set height. - MP 05/08/2018

            // if we have set a scroll jump position for document, do not revert scroll position
            if (!hasCustomScroll()) {
                if (sp !== null && sp + window.innerHeight + 400 < wh) {
                    // 400px buffer
                    logger.log('setting sp in pageloaded');
                    document.documentElement.scrollTop = sp;

                    wh = 0;
                    sp = null;
                }
            }
        });

        instance.add_endRequest((sender, args) => {
            // if we have set a scroll jump position for document, do not revert scroll position
            if (!hasCustomScroll() && sp !== null) {
                logger.log('setting sp in endRequest');
                document.documentElement.scrollTop = sp;

                wh = 0;
                sp = null;
            }

            // reset scroll jump queue now that the postback is complete
            window.scrollJump.reset();

            window.removeLoadingState();
            if (args && args._errorHandled === false && !isNull(args._error)) {
                window.async_translate('One or more errors occurred. Please try again. If the issue persists, please contact our Support Team.')
                    .then((error) => {
                        alertToast.show(error);
                        logger.log('There was a failed partial postback');
                        logger.log('Sender:', sender);
                        logger.log('Args:', args);
                    });
            }
        });
    }
});
// #endregion
// #region onresize
$(window).on('resize', () => {
    // prevent resize if window width is the same as last time window resize was called
    if (window.currentWindowWidth === window.getWindowWidth()) {
        return;
    }
    window.cacheLayoutData();

    // handle microNav visibility
    microNav.handleResize();

    window.layoutQueue.run();

    // set window size in server session variable
    window.doSetClientInfoPostDebounced();
});
// #endregion
// #region onunload
$(window).on('unload', () => {
    sessionStorage.previousPagePath = window.location.pathname;
});

window.getView = async (componentName: string, settings: object = {}) => {
    const vueInit = await import('@/vue/shared/vueInit').then((res) => res.default);

    const component = await import(
        /* webpackChunkName: "[request]" */ `@/vue/views/${componentName}.vue`
    ).then((res) => res.default);

    return vueInit(
        component,
        // component options
        {},
        // vue instance settings
        settings
    );
};

window.loadComponentInstance = async (componentName: string, componentOptions: object, settings: Partial<VueSettings>) => {
    if (!settings.mountNode) {
        throw new Error('mountNode is required when loading a single instance component');
    }

    const component = await import(
        /* webpackChunkName: "[request]" */ `@/vue/components/${componentName}`
    ).then((res) => res.default);

    // componentPath, componentOptions, context, mountNode
    return vueInit(
        component,
        componentOptions,
        settings,
    );
};

window.loadLoggedOutEmbarkHeader = async (mountNodeId: string) => {
    mountLoggedOutEmbarkHeader(mountNodeId);
};

window.loadAmazonConnector = async (mountNodeId: string) => {
    mountAmazonConnector(mountNodeId);
};

window.renderReactView = renderReactView;

window.renderEmbark = renderEmbark;

window.mountSiteFooter = mountSiteFooter;

const updateSideNavOffset = () => {
    const doc = document.documentElement;
    const el = document.getElementById('sidenav_offset');

    const SIDE_NAV_WIDTH = 251;
    const containerMarginSingle = window.getContainerMargin() / 2;

    // multiply by 2 because sidenav may occupy margin from both sides if needed.
    const offset = (SIDE_NAV_WIDTH - containerMarginSingle) * 2;
    const width =  Math.max(0, Math.min(SIDE_NAV_WIDTH, offset));

    if (el) {
        if (doc.classList.contains('has-embark-sidenav-open') && window.getWindowWidth() >= 960) {
            el.style.minWidth = width + 'px';
        } else {
            el.style.minWidth = 'auto';
        };
    }

    if (!doc.classList.contains('has-topnav-loaded')) {
        doc.classList.add('has-topnav-loaded');
    }
}
window.updateSideNavOffset = updateSideNavOffset;

// tool used to track user data - https://www.pendo.io/ - https://emburse.atlassian.net/browse/CER-10656

// function to decide whether to init pendo for the current environment
// it should be true only if neither IsQAEnvironment nor IsDevEnvironment is true (i.e. prod)
// or if there is a localstorage flag set to true for testing purposes
const shouldInitPendo = () => {
    const flags = window.AccountConfig?.UIFlags;
    // account config doesn't have a specific flag for prod, but pendo should only be initialized 
    // if we are in prod, which is when both IsQAEnvironment and IsDevEnvironment are false
    if (flags && !flags.IsQAEnvironment && !flags.IsDevEnvironment) {
        return true;
    }

    const localStorageFlag = localStorage.getItem('pendoDevEnabled');
    return localStorageFlag === 'true';
}

window.initPendo = () => (function (apiKey) {
    if (!isUserLoggedIn() || !shouldInitPendo()) {
        return;
    }

    (function (p, e, n, d, o) {
        let v; let w; let x; let y; let z; o = p[d] = p[d] || {}; o._q = o._q || [];
        v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track']; for (w = 0, x = v.length; w < x; ++w) {
            (function (m) {
                o[m] = o[m] || function () { o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0))); };
            }(v[w]));
        }
        y = e.createElement(n); y.async = !0; y.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
        z = e.getElementsByTagName(n)[0]; z.parentNode.insertBefore(y, z);
    }(window, document, 'script', 'pendo'));

    const { UseMobileSso, EmpAdminLevel, IsCertifyMobile, ForteACHOnFile, UseInvoice, CompanyUseCAPPO, ...visitorUIFlags } = window.AccountConfig.UIFlags;

    let companyData = {};

    if (window.AccountConfig.Emp.Company?.UID) {
        companyData = {
            id: `ce_${window.AccountConfig.Emp.Company.UID}`,
            name: window.AccountConfig.Emp.Company.Name,
            creationDate: window.AccountConfig.Emp.Company.CreateDate,
            client_id: window.AccountConfig.Emp.Company?.AccountNumber ? window.AccountConfig.Emp.Company.AccountNumber : '',
            hasActiveCompanyCreditCardProgram: window.AccountConfig.Company_HasActiveCreditCardProgram,
            nexoniaMigratedCustomer: Boolean(window.AccountConfig.NexoniaMigratedCustomer),
            shardId: window.AccountConfig.ShardId,
            companyStatusName: window.AccountConfig.Company_CompanyStatusName,
            useMobileSso: UseMobileSso,
            enableAssuranceAIRules: window.AccountConfig.Company_EnableAssuranceAIRules,
            showLegacyInstantAuditRules: window.AccountConfig.Company_ShowLegacyInstantAuditRules,
            useCertifyAP: CompanyUseCAPPO,
            useInvoiceClassic: UseInvoice,
        };
    }

    window.pendo.initialize({
        visitor: {
            id: `ce_${window.AccountConfig.Emp.UID}`,
            email: window.AccountConfig.Emp.Email.toLowerCase(),
            admin: EmpAdminLevel === 'Full',
            em_language: window.AccountConfig.LanguageCode,
            full_name: window.AccountConfig.Emp.FullName,
            creationDate: window.AccountConfig.Emp.CreateDate,
            role: window.AccountConfig.Emp.EmpType,
            is_certify_mobile: IsCertifyMobile,
            ForteACHOnFile: ForteACHOnFile,
            ...visitorUIFlags,
        },
        account: companyData,
    });
}('12ba0d72-43e0-4826-7cfb-b9266be44ddc'));

if (window.SentryConfig) {
    sentryInit(window.SentryConfig);
}
